// El idioma de la app.
//
// ═══════════════════════════════════════════════════════════════════
// LA REGLA QUE ORDENA TODO ESTE ARCHIVO
// ═══════════════════════════════════════════════════════════════════
//
// Ya estaba escrita, en `lib/opciones.js` líneas 7-10, desde antes de
// que existiera este módulo:
//
//   "Los valores se guardan en español. La traducción se resuelve en la
//    capa de presentación: se traduce la etiqueta que ve el usuario,
//    nunca el valor almacenado. Por eso la clave guardada nunca cambia
//    aunque la app hable 10 idiomas."
//
// O sea: en la base dice `"Superior"` para siempre. En pantalla dice
// "Superior", "Top" o "Parte de cima" según quién mire. **No hay
// migración de datos, no se toca ningún CHECK constraint, y las 61
// tablas indexadas por valor literal (`clima.js`, `tintes.js`,
// `estilos.js`) siguen funcionando sin tocarse**, porque todas indexan
// por la clave almacenada.
//
// ⚠️  SI ALGUNA VEZ SE TRADUCE UN VALOR ALMACENADO, SE ROMPE EL ARMARIO
//     DE TODOS. Los CHECK de `supabase/schema.sql` validan los valores
//     en español; una fila con `tipo = 'Top'` es rechazada por Postgres.
//     Esa es la línea que no se cruza.
//
// ═══════════════════════════════════════════════════════════════════
// POR QUÉ NO SE USA i18next, QUE ES LO QUE DECÍA EL PLAN
// ═══════════════════════════════════════════════════════════════════
//
// El comentario de `lib/opciones.js` nombra i18next. Se desvió a
// propósito y conviene que quede escrito, porque es una decisión
// reversible y alguien va a querer saber por qué.
//
//   · Lo que hacía falta de i18next es la pluralización por idioma, y
//     eso hoy lo da el navegador: `Intl.PluralRules`. No existía cuando
//     i18next se volvió el estándar.
//   · Este módulo entero entra en una pantalla y se puede leer. Ese es
//     el criterio de este proyecto para las dependencias.
//   · i18next son ~15 KB comprimidos sobre un bundle de 91.
//
// **Cuándo habría que cambiar de idea:** si aparece la necesidad de
// formato ICU (`{count, plural, one{...} other{...}}` anidado), de
// interpolación con componentes de React dentro del texto, o si los
// strings pasan de unos pocos miles. Ahí i18next deja de ser peso
// muerto y pasa a ahorrar trabajo.
//
// (La carga perezosa por idioma también estaba en esa lista y salió: se
// implementó acá abajo en 30 líneas usando `import()`, que es lo mismo
// que usa i18next por dentro.)
//
// ═══════════════════════════════════════════════════════════════════

import { useSyncExternalStore } from "react";

/* ── CÓMO SE CARGAN LOS IDIOMAS, Y POR QUÉ ASÍ ─────────────────────
 *
 * Español va en el bundle. Los demás se bajan cuando alguien los pide.
 *
 * ⚠️  ESTA DECISIÓN ES GRATIS HOY Y CARA EN SEIS MESES. Con dos idiomas
 *     la diferencia son 2 KB y no la nota nadie. Pero si veinte
 *     pantallas se acostumbran a que la tabla esté disponible de forma
 *     síncrona, volverla asíncrona después es refactorizar veinte
 *     archivos. Por eso se hace ahora, con dos idiomas y sin apuro.
 *
 * La cuenta con 30.000 usuarios: seis idiomas cargados de una serían
 * ~24 KB comprimidos que baja TODO el mundo para usar la sexta parte.
 * A ~20 aperturas por usuario al mes son 600.000 descargas de 20 KB
 * inútiles = ~12 GB/mes de egreso de Railway por nada.
 *
 * Español queda estático a propósito, por dos razones:
 *   1. Es el respaldo de `t()`. Si tuviera que viajar, la primera
 *      pantalla no tendría con qué responder.
 *   2. Es el idioma de la mayoría hoy: para ellos el costo es CERO,
 *      ni un pedido más.
 *
 * Mientras el archivo viaja, `t()` devuelve el respaldo en español y la
 * pantalla se pinta. Cuando llega, avisa a los oyentes y se repinta. No
 * hay pantalla en blanco esperando una traducción. */
/* ⚠️  INGLÉS TAMBIÉN VA ESTÁTICO DESDE EL 2026-09-05, Y ES EL MISMO
 *     ARGUMENTO DE ARRIBA APLICADO AL IDIOMA QUE CAMBIÓ DE LUGAR.
 *
 * Desde hoy, **todo el que llega sin haber elegido idioma ve inglés**
 * (decisión del dueño). Si el inglés viajara como chunk, la pantalla de
 * entrada —la primera que ve cualquier visitante— se pintaría un
 * instante con el respaldo en español y después saltaría a inglés. Ese
 * parpadeo pasa en la peor pantalla posible: la única que ve alguien
 * que todavía no decidió si se registra.
 *
 * Es exactamente la razón 2 de acá abajo, movida: "es el idioma de la
 * mayoría hoy" ya no describe al español para un visitante nuevo.
 *
 * Cuesta ~53 KB sin comprimir. El español sigue estático por la razón
 * 1, que no cambió: es el respaldo de `t()` y está escrito en el
 * código fuente de cada pantalla. */
import es from "./idiomas/es";
import en from "./idiomas/en";

const CARGADORES = {
  es: null, // estático: va en el bundle
  en: null, // estático también: es lo que ve un visitante nuevo
  pt: () => import("./idiomas/pt"),
  fr: () => import("./idiomas/fr"),
  ru: () => import("./idiomas/ru"),
  zh: () => import("./idiomas/zh"),
  ja: () => import("./idiomas/ja"),
  ko: () => import("./idiomas/ko"),
  hi: () => import("./idiomas/hi"),
  bn: () => import("./idiomas/bn"),
  ur: () => import("./idiomas/ur"),
  ar: () => import("./idiomas/ar"),
  /* Los doce que ofrece la app (`lib/rutas_perfil.js` → IDIOMAS_APP).
   * Cada uno es un chunk aparte de ~2,5 KB: nadie baja los doce.
   * `test_i18n.js` falla si aparece un archivo que no está acá — un
   * idioma traducido que nadie puede ver. */
};

/* Los códigos son los mismos que acepta `perfiles.idioma`
 * (supabase/perfil.sql: check (idioma in ('es','en','pt'))). Si se
 * agrega uno acá sin agregarlo allá, el perfil lo rechaza; si se agrega
 * allá sin agregarlo acá, cae a español. `test_i18n.js` mira las dos
 * listas. */
const TABLAS = { es, en };

/* El respaldo de la TABLA: si se pide un idioma cuya tabla todavía no
 * llegó, se usa esta. No es lo mismo que el idioma que ve un visitante
 * (abajo), y confundirlos fue lo que hizo falta separar. */
const POR_DEFECTO = "es";

/* ⚠️  LO QUE VE ALGUIEN QUE NUNCA ELIGIÓ NADA. Decisión del dueño el
 *     2026-09-05: inglés, siempre, venga de donde venga.
 *
 * No se mira `navigator.language` a propósito — se ofreció y se
 * descartó. Un visitante paraguayo con el navegador en español ve
 * inglés y tiene que usar el selector; a cambio, lo que ve el primer
 * visitante no depende de la configuración de su máquina, que es
 * imposible de reproducir cuando alguien reporta algo. */
const IDIOMA_VISITANTE = "en";

/* Los doce, con su nombre EN SU PROPIO IDIOMA. Es una copia de
 * `IDIOMAS_APP` de lib/rutas_perfil.js, y la copia es a propósito: la
 * pantalla de entrada no tiene sesión y no puede pedirle la lista al
 * servidor sin agregar un viaje de red justo antes del primer pintado.
 *
 * ⚠️  `test_idioma_visitante.js` compara las dos listas —códigos, orden
 *     y nombres— y se pone rojo si difieren. Una copia vigilada es
 *     aceptable; una copia suelta es la que un día dice otra cosa. */
export const IDIOMAS = [
  { codigo: "es", nativo: "Español" },
  { codigo: "en", nativo: "English" },
  { codigo: "pt", nativo: "Português" },
  { codigo: "fr", nativo: "Français" },
  { codigo: "ru", nativo: "Русский" },
  { codigo: "zh", nativo: "中文" },
  { codigo: "ja", nativo: "日本語" },
  { codigo: "ko", nativo: "한국어" },
  { codigo: "hi", nativo: "हिन्दी" },
  { codigo: "bn", nativo: "বাংলা" },
  { codigo: "ur", nativo: "اردو" },
  { codigo: "ar", nativo: "العربية" },
];

/* ── LA MEMORIA DEL IDIOMA ─────────────────────────────────────────
 *
 * "Una vez que el visitante eligió un idioma, la página lo mantiene en
 * memoria" (dueño, 2026-09-05). Vive en `localStorage`, al lado de la
 * sesión, y sobrevive a cerrar el navegador.
 *
 * ⚠️  TODO ACCESO VA ENVUELTO EN try/catch, Y NO ES PARANOIA.
 *     `localStorage` LANZA —no devuelve null, lanza— en una ventana
 *     privada de Safari y con las cookies de terceros bloqueadas. Una
 *     excepción acá, que corre al cargar el módulo, dejaría la app
 *     entera en blanco por no poder recordar un idioma. */
const CLAVE_IDIOMA = "brujel.idioma";

function idiomaRecordado() {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(CLAVE_IDIOMA);
  } catch {
    return null;
  }
}

function recordarIdioma(codigo) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(CLAVE_IDIOMA, codigo);
  } catch {
    /* Sin memoria, la app funciona igual: cada visita arranca en
     * inglés. Es peor, no está roto. */
  }
}

/* Lo recordado manda sobre el default, y un valor que no reconocemos
 * NO manda sobre nada: alguien puede editar localStorage a mano, y un
 * código inventado tiene que caer en inglés y no dejar la pantalla sin
 * texto. */
const guardado = idiomaRecordado();

let actual =
  guardado && Object.prototype.hasOwnProperty.call(CARGADORES, guardado)
    ? guardado
    : IDIOMA_VISITANTE;
let tabla = TABLAS[actual] || TABLAS[POR_DEFECTO];

/* Quién se entera cuando cambia el idioma.
 *
 * Sin esto habría que recargar la página para ver el cambio, y recargar
 * pierde lo que la persona estuviera escribiendo. */
const oyentes = new Set();

/* ⚠️  ESTE CONTADOR NO ES UN DETALLE DE IMPLEMENTACIÓN: ES EL ARREGLO.
 *
 * La primera versión de `useIdioma` guardaba el CÓDIGO del idioma como
 * estado de React: `useState(actual)` y, en cada aviso, `marcar(codigo)`.
 * Parece lo obvio y está mal de una manera que no produce ningún error.
 *
 * Cambiar de español a inglés avisa DOS veces, y tiene que ser así:
 *
 *   1. Al elegirlo. `ponerIdioma("en")` marca el idioma y PIDE el
 *      archivo. Avisa: `marcar("en")`. El estado pasa de "es" a "en",
 *      la pantalla se repinta… en español, porque el archivo todavía
 *      está viajando. Correcto: mejor español que una pantalla vacía.
 *   2. Al llegar el archivo. `aplicar("en")` ya tiene la tabla y avisa
 *      de nuevo: `marcar("en")`. **El estado ya valía "en", así que
 *      React no repinta nada.** No es un bug de React: es una
 *      optimización suya, documentada, y correcta en general.
 *
 * El aviso que importaba —el segundo— se perdía. La pantalla se quedaba
 * en español hasta que cualquier otra cosa la obligara a repintarse.
 *
 * Y ESO EXPLICA EL SÍNTOMA RARO, que es lo que costó entender: navegar
 * a otra pestaña repinta, así que casi toda la app se veía traducida.
 * La excepción era el Perfil — la única pantalla que se mira SIN
 * navegar, porque es un modal y el selector de idioma vive adentro. Se
 * reportó como "el Perfil no se traduce" y no era del Perfil.
 *
 * El contador NUNCA repite valor, así que ningún aviso se puede perder.
 * `useSyncExternalStore` es la pieza de React hecha para esto: compara
 * la foto (el número), no el idioma. */
let version = 0;

function avisar() {
  version += 1;
  for (const f of oyentes) f();
}

function suscribir(f) {
  oyentes.add(f);
  return () => oyentes.delete(f);
}

export function idiomaActual() {
  return actual;
}

export function idiomasDisponibles() {
  return Object.keys(CARGADORES);
}

/* IDIOMAS QUE SE LEEN DE DERECHA A IZQUIERDA.
 *
 * De los doce que ofrece la app, dos: árabe y urdu. Persa y hebreo
 * están acá por si algún día entran — cuesta lo mismo ahora que
 * después, y descubrirlo tarde es descubrirlo con la pantalla al revés.
 *
 * ⚠️  ESTA LÍNEA SOLA NO ALCANZA, PERO ES LA QUE ENCIENDE TODO LO DEMÁS.
 *     Poner `dir="rtl"` en el <html> no espeja nada por sí mismo: lo
 *     espeja el CSS, y solo si está escrito con propiedades LÓGICAS
 *     (`margin-inline-start`, `inset-inline-end`, `text-align: start`)
 *     en vez de físicas (`margin-left`, `right`, `text-align: left`).
 *     El 2026-08-25 se convirtieron las 29 que había. Si alguien
 *     escribe `margin-left` de nuevo, ese pedazo de pantalla queda al
 *     revés en árabe y en ningún otro lado — o sea, nadie lo va a ver
 *     hasta que se queje un usuario. Hay un test que lo mira
 *     (`test_css.js`). */
const RTL = new Set(["ar", "ur", "fa", "he"]);

export function esRTL(codigo) {
  return RTL.has(String(codigo || "").slice(0, 2).toLowerCase());
}

function aplicar(codigo) {
  if (codigo === actual && tabla === TABLAS[codigo]) return;
  actual = codigo;
  tabla = TABLAS[codigo] || TABLAS[POR_DEFECTO];
  marcarDocumento(codigo);
  avisar();
}

/* `lang` y `dir` van juntos SIEMPRE.
 *
 * `lang` no es decorativo: de él dependen la separación silábica, el
 * corrector del navegador, la voz de un lector de pantalla y qué fuente
 * elige el sistema para el chino contra el japonés — los dos usan
 * caracteres que se dibujan distinto según el idioma declarado. */
/* El `og:locale` que pide Open Graph: idioma_TERRITORIO. No hay
 * territorio "correcto" para un idioma que se habla en veinte países;
 * se elige el más poblado de cada uno, que es lo que hacen las redes
 * para elegir la variante del texto. */
const LOCALES = {
  es: "es_ES",
  en: "en_US",
  pt: "pt_BR",
  fr: "fr_FR",
  ru: "ru_RU",
  zh: "zh_CN",
  ja: "ja_JP",
  ko: "ko_KR",
  hi: "hi_IN",
  bn: "bn_BD",
  ur: "ur_PK",
  ar: "ar_AR",
};

/* `lang`, `dir`, el TÍTULO de la pestaña y `og:locale` van juntos.
 *
 * ⚠️  EL TÍTULO ESTABA EN INGLÉS PARA LOS DOCE IDIOMAS (auditoría
 *     2026-09-15, §3). `index.html` lo trae en inglés —es el que leen
 *     los buscadores, que no ejecutan JavaScript, y la app abre en
 *     inglés para quien todavía no eligió— pero una vez que la persona
 *     eligió su idioma, la pestaña, el historial del navegador y el
 *     marcador que guarde seguían en inglés.
 *
 *     Se arma con el eslogan, que ya está traducido en los doce: no se
 *     inventa una frase nueva por idioma que después nadie mantenga.
 *
 * ⚠️  `hreflang` NO SE PONE, Y ES UNA DECISIÓN. Los doce idiomas viven
 *     en UNA sola URL y se eligen en el navegador; `hreflang` describe
 *     URLs distintas por idioma, que acá no existen. Ponerlo apuntando
 *     doce veces a la misma dirección sería declarar algo falso. Lo que
 *     el rastreador lee es la versión en inglés, que es la que abre.
 */
function marcarDocumento(codigo) {
  const raiz = document.documentElement;
  raiz.lang = codigo;
  raiz.dir = esRTL(codigo) ? "rtl" : "ltr";

  /* ⚠️  ESTE ATRIBUTO ES LO QUE DESTRABA FRAUNCES, Y EXISTE POR UNA
   *     CARRERA MEDIDA, NO POR ELEGANCIA (24/9/2026).
   *
   * `sistema.css` da Fraunces solo a los cuatro idiomas latinos, con
   * `:root:lang(es)` y compañía. Pero `public/index.html` se sirve con
   * `lang="en"` fijo —correcto, porque el bloque que ven los
   * rastreadores está en inglés— así que entre que el navegador aplica
   * la hoja y que esta función corre, la raíz DICE inglés aunque la
   * persona lea en árabe. En esa rendija el navegador ya pidió el
   * .woff2: 36,6 KB por una fuente que ese idioma no va a usar.
   *
   * Con el atributo, la regla de Fraunces exige `[data-montado]`, que
   * no existe hasta que esta función corre — y cuando corre, `lang` ya
   * es el de verdad. Antes de eso nada nombra la familia y el archivo
   * no se pide. Medido: en árabe, ruso, japonés y bengalí el navegador
   * no descarga ninguna fuente.
   *
   * No sirve para nada más. Si alguna vez se borra, no se rompe nada
   * visible: simplemente vuelve a bajar la fuente en los ocho idiomas
   * que no la usan. Por eso lo cuida `test_css.js` §16. */
  /* El `if` por lo mismo que el `?.` de más abajo: `test_idioma_visitante.js`
   * monta un `document` mínimo, sin `dataset`, y un atributo de más no
   * puede tumbar el cambio de idioma. */
  if (raiz.dataset) raiz.dataset.montado = "1";

  const eslogan = t("portada.eslogan", "Ya lo tienes.");
  document.title = `Brujel — ${eslogan}`;

  /* `?.` porque esto también corre fuera de un navegador de verdad: los
   * tests montan un `document` mínimo (`test_idioma_visitante.js`), y un
   * `meta` que no está no puede tumbar el cambio de idioma. */
  const og = document.querySelector?.('meta[property="og:locale"]');
  if (og) og.setAttribute("content", LOCALES[codigo] || LOCALES.en);
}

/**
 * Cambia el idioma. Devuelve el código que quedó puesto AHORA — que
 * puede no ser el pedido todavía, si el archivo está viajando.
 *
 * No hace falta esperar el resultado: cuando el archivo llega, los
 * oyentes se enteran solos y la pantalla se repinta.
 */
export function ponerIdioma(codigo) {
  const limpio = String(codigo || "").toLowerCase().slice(0, 5);

  /* Un idioma que no tenemos NO es un error: es un perfil que pide algo
   * que todavía no existe (la base acepta 'pt' y acá puede no estar).
   * Cae a español, que es peor que traducido y mucho mejor que vacío. */
  if (!CARGADORES.hasOwnProperty(limpio)) {
    /* Cae en el idioma del visitante y NO en el respaldo de la tabla:
     * desde que el default es inglés, mandar a alguien al español
     * porque su perfil pide un idioma que todavía no existe sería
     * elegir por él el idioma equivocado. */
    aplicar(IDIOMA_VISITANTE);
    return actual;
  }

  /* Se recuerda ACÁ y no en el selector, para que valga para todas las
   * puertas por las que se cambia el idioma: el selector de la pantalla
   * de entrada, el del perfil, y el idioma que trae el perfil al
   * entrar. Con esto, alguien que puso su cuenta en francés y vuelve a
   * la pantalla de login la ve en francés. */
  recordarIdioma(limpio);

  // Ya cargado (o es el estático): cambio inmediato, sin viaje.
  if (TABLAS[limpio]) {
    aplicar(limpio);
    return actual;
  }

  /* Todavía no está. Se marca el idioma —para que `document.lang` y los
   * plurales de `Intl` sean los correctos desde ya— y se pide el
   * archivo. Hasta que llegue, `t()` responde con el respaldo español.
   *
   * Si falla la descarga (red caída, chunk borrado por un deploy viejo)
   * no se rompe nada: se queda en español. Un idioma que no llegó es un
   * problema de red, no una razón para dejar la app sin texto. */
  actual = limpio;
  marcarDocumento(limpio);

  /* El inglés es el respaldo intermedio (ver `buscar`). Se pide junto
   * con el idioma elegido y no después, para que las dos redes de
   * seguridad lleguen a la vez. */
  if (limpio !== "en" && !TABLAS.en && CARGADORES.en) {
    CARGADORES.en()
      .then((mod) => {
        TABLAS.en = mod.default || mod;
        if (actual !== "en") avisar();
      })
      .catch(() => {});
  }

  CARGADORES[limpio]()
    .then((mod) => {
      TABLAS[limpio] = mod.default || mod;
      if (actual === limpio) aplicar(limpio);
    })
    .catch(() => {
      /* Silencio a propósito: la app ya está funcionando en español. */
    });
  avisar();
  return actual;
}

/**
 * Deja el documento marcado con el idioma que corresponde ANTES del
 * primer pintado. Se llama una vez, desde src/index.js.
 *
 * ⚠️  NO ALCANZA CON QUE `actual` YA VALGA LO CORRECTO.
 *
 * `actual` se resuelve al cargar el módulo (memoria o inglés), pero
 * `<html lang>` y `<html dir>` los pone `marcarDocumento`, y a esa la
 * llama `aplicar`, que corta de entrada si el idioma no CAMBIÓ. O sea
 * que sin esto, alguien que dejó la app en árabe volvía a una pantalla
 * de entrada con el texto en árabe y la maquetación de izquierda a
 * derecha: todo al revés, y ninguna excepción en ningún lado.
 */
export function arrancarIdioma() {
  marcarDocumento(actual);
  // Si el idioma recordado no está en el bundle (todo lo que no sea
  // inglés ni español), se pide acá. Hasta que llegue se ve el respaldo.
  if (!TABLAS[actual]) ponerIdioma(actual);
  return actual;
}

/** Re-renderiza el componente cuando cambia el idioma. */
export function useIdioma() {
  /* La "foto" es el contador, no el idioma: ver el comentario de
   * `version` arriba. Si esto vuelve a devolver `actual`, el aviso de
   * la llegada del archivo se pierde igual que antes — React compara
   * la foto y las dos serían "en". `test_idioma_vivo.js` lo vigila. */
  useSyncExternalStore(suscribir, () => version, () => version);
  return actual;
}

function enTabla(t0, clave) {
  // Claves con punto: "catalogo.color.Azul" → tabla.catalogo.color.Azul
  let nodo = t0;
  for (const parte of String(clave).split(".")) {
    if (nodo == null || typeof nodo !== "object") return undefined;
    nodo = nodo[parte];
  }
  return typeof nodo === "string" ? nodo : undefined;
}

/* LA CADENA DE RESPALDO: idioma pedido → inglés → lo que pase el que
 * llama (que es el texto en español del código fuente).
 *
 * ⚠️  EL INGLÉS EN EL MEDIO APARECIÓ CON LOS 13 IDIOMAS.
 *
 *     Con dos idiomas el respaldo era español y no molestaba a nadie:
 *     el que no leía inglés leía español. Con trece, una clave sin
 *     traducir le muestra español a alguien que eligió japonés — un
 *     idioma que probablemente no lee. El inglés no es mejor por ser
 *     inglés: es el que más gente puede descifrar cuando el suyo falla.
 *
 * Por eso `ponerIdioma` carga TAMBIÉN el inglés cuando el idioma pedido
 * no es ni español ni inglés. Es un archivo más para quien eligió un
 * tercer idioma, y cero para todos los demás. */
function buscar(clave) {
  const propio = enTabla(tabla, clave);
  if (propio !== undefined) return propio;

  /* ⚠️  EL INGLÉS NO ES RESPALDO DEL ESPAÑOL, Y ESTA LÍNEA FALTABA.
   *     (2026-09-10 — toda la interfaz en español salió en inglés
   *     durante días, y a nadie le apareció un error.)
   *
   * `es.js` NO tiene claves de interfaz, a propósito: el español viaja
   * como el `respaldo` que cada pantalla le pasa a `t()` en el código
   * fuente. Es el ORIGINAL, no una traducción que falta.
   *
   * Con la cadena de arriba sin esta rama, cada clave de interfaz de un
   * usuario en español no se encontraba en `es.js` —correcto—, se
   * buscaba en `en.js` —donde SÍ está— y se devolvía en inglés. El
   * `respaldo` en español, que estaba ahí mismo, no se usaba nunca.
   *
   * Y el síntoma era engañoso: `catalogo`, `estilo` y `error` SÍ están
   * en `es.js`, así que la pantalla salía mitad y mitad —títulos y
   * botones en inglés, prendas y estaciones en español—, que se lee
   * como "faltan traducciones al inglés" y es exactamente al revés.
   * Se reportó así, y llevó una tarde entender que la app no estaba en
   * inglés: estaba en español.
   *
   * Peor: en español la portada también salía en inglés, así que nadie
   * podía notar que su idioma estaba mal puesto. El selector andaba y
   * no se veía que anduviera.
   *
   * `test_bienvenida_idiomas.js` §0a dibuja la bienvenida en español y
   * exige encontrar palabras en español. Fue lo que lo encontró. */
  if (String(actual).slice(0, 2) === "es") return undefined;

  const ingles = TABLAS.en ? enTabla(TABLAS.en, clave) : undefined;
  return ingles;
}

/**
 * El texto de una clave.
 *
 * ⚠️  EL RESPALDO NO ES OPCIONAL POR CAPRICHO.
 *
 * Cuando falta una traducción hay dos formas de fallar:
 *
 *   · mostrar la clave       → "catalogo.color.Azul" en pantalla
 *   · mostrar el respaldo    → "Azul", en español, en una app en inglés
 *
 * La segunda es fea; la primera parece un error del programa y hace que
 * la persona desconfíe de todo lo demás que ve. Es la asimetría de
 * siempre: de más se corrige, de menos se rompe.
 *
 * Por eso quien llama SIEMPRE pasa el texto en español como respaldo, y
 * por eso `t()` nunca devuelve la clave.
 *
 * @param {string} clave    p.ej. "carga.arrastra"
 * @param {string} respaldo el texto en español
 * @param {object} [params] para los `{marcadores}` del texto
 */
export function t(clave, respaldo, params) {
  const texto = buscar(clave) ?? respaldo ?? "";
  if (!params) return texto;
  return texto.replace(/\{(\w+)\}/g, (crudo, nombre) => {
    const valor = params[nombre];
    if (valor === undefined) return crudo;
    /* ⚠️  UN NÚMERO SE ESCRIBE EN EL IDIOMA DE LA PANTALLA (auditoría
     * 2026-09-15, §3: «dígitos mezclados en bengalí»).
     *
     * `numero()` y `tPlural()` ya usaban `toLocaleString`, así que en
     * bengalí una pantalla mostraba «১,২৩৪» en un renglón y «1234» en el
     * de al lado, del mismo dato. Lo mismo en árabe y en urdu, que
     * escriben sus propios dígitos, y en los idiomas que separan los
     * miles con punto en vez de coma.
     *
     * Solo los números: un texto se interpola tal cual. */
    return typeof valor === "number" && Number.isFinite(valor)
      ? valor.toLocaleString(actual)
      : String(valor);
  });
}

/**
 * La etiqueta de un valor de catálogo.
 *
 * El valor ES la clave: `tCat("color", "Azulclaro")` busca
 * `catalogo.color.Azulclaro` y, si no está, muestra "Azulclaro".
 *
 * Y ese respaldo ya es una mejora visible hoy, antes de traducir nada:
 * las claves de color están escritas pegadas ("Azulclaro",
 * "MarrónOscuro", "Petroleo") porque nacieron como claves, y la app las
 * venía mostrando así, tal cual, a la persona. El archivo `es` las
 * separa: "Azul claro", "Marrón oscuro", "Petróleo".
 */
export function tCat(grupo, valor) {
  if (valor == null || valor === "") return "";
  return t(`catalogo.${grupo}.${valor}`, String(valor));
}


/**
 * Plural por idioma, no por `n === 1`.
 *
 * ⚠️  LOS 18 PLURALES DE ESTA APP ESTÁN ESCRITOS `n === 1 ? "" : "s"`.
 *     Eso funciona en español y en inglés, y solo ahí. El ruso y el
 *     polaco tienen tres formas; el árabe, seis; el japonés, una.
 *     `Intl.PluralRules` las conoce todas y las da el navegador.
 *
 * Las formas se declaran en el archivo de idioma como un objeto:
 *
 *     prendas: { one: "{n} prenda", other: "{n} prendas" }
 *
 * Español e inglés solo usan `one` y `other`. Un idioma que necesite
 * `few` o `many` los agrega en SU archivo, sin tocar los demás ni el
 * código que llama.
 */
export function tPlural(clave, n, respaldos, params) {
  const cantidad = Number(n) || 0;
  let formas = tabla;
  for (const parte of String(clave).split(".")) {
    formas = formas == null ? undefined : formas[parte];
  }
  if (!formas || typeof formas !== "object") formas = respaldos;

  let forma = "other";
  try {
    forma = new Intl.PluralRules(actual).select(cantidad);
  } catch {
    /* Un navegador sin Intl.PluralRules para este idioma: `other` es la
     * forma que todo idioma tiene. */
  }

  const texto =
    formas?.[forma] ?? formas?.other ?? respaldos?.[forma] ?? respaldos?.other ?? "";
  return texto.replace(/\{(\w+)\}/g, (crudo, nombre) => {
    /* `{n}` sale formateado en el idioma (1.234 / 1,234 / ١٬٢٣٤): antes
     * era `String(cantidad)` y «200000 combinaciones» no tenía separador
     * de miles en ningún idioma. */
    {
      if (nombre === "n") return cantidad.toLocaleString(actual);
      const valor = params?.[nombre];
      if (valor === undefined) return crudo;
      /* Los OTROS parámetros también pueden ser números —«{registrados}
       * de {n} días con registro»— y también tienen que escribirse en
       * el idioma de la pantalla. Ver el comentario de `t()`. */
      return typeof valor === "number" && Number.isFinite(valor)
        ? valor.toLocaleString(actual)
        : String(valor);
    }
  });
}

/* Fechas y números salen del idioma actual y no de `"es"` cableado.
 *
 * ⚠️  Ya no queda ningún `toLocale*("es")` en src/ (los últimos dos, en
 *     Historial.js, salieron el 2026-09-18). `test_i18n.js` 14 tiene
 *     techo CERO: el próximo que aparezca pone la suite en rojo. Un
 *     comentario que decía «hay 13» durante semanas fue lo que dejó
 *     esos dos sin mirar. */
export function fecha(valor, opciones) {
  const d = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(actual, opciones);
}

/**
 * El texto de un error que vino del servidor.
 *
 * ⚠️  EL SERVIDOR NO HABLA NINGÚN IDIOMA. Manda un código y un texto en
 *     inglés: ver el encabezado de `lib/errores_codigos.js`. Acá se
 *     decide qué ve la persona.
 *
 *     · Hay traducción del código → se usa.
 *     · No hay → el inglés que mandó el servidor, ya interpolado.
 *     · El servidor es viejo y no manda código → su texto, tal cual.
 *
 *     Nunca se muestra el código crudo. `PHOTO_NOT_YOURS` en pantalla
 *     es lo que hace que una app parezca rota.
 *
 * `datos` son las piezas sueltas (un máximo, una cantidad) para que
 * cada idioma arme SU frase: en alemán el número no va en el mismo
 * lugar que en inglés, así que una frase ya armada no sirve.
 */
export function tError(e) {
  const respaldo =
    (typeof e === "string" ? e : e?.error) ||
    t("error.OPERATION_FAILED", "No se pudo completar la operación. Prueba de nuevo.");
  const codigo = typeof e === "object" ? e?.codigo : null;
  if (!codigo) return respaldo;
  return t(`error.${codigo}`, respaldo, e?.datos);
}

export function numero(valor, opciones) {
  const n = Number(valor);
  if (Number.isNaN(n)) return "";
  return n.toLocaleString(actual, opciones);
}

/**
 * Une una lista para leerla: "a", "a y b", "a, b y c".
 *
 * ⚠️  EXISTE PORQUE `join(" y ")` ES ESPAÑOL ESCRITO A MANO.
 *
 * `src/Comprar.js` hacía `datos.zonasSinCubrir.join(" y ")`, y eso son
 * DOS fugas de idioma en la misma línea: la conjunción castellana, y
 * los valores crudos del servidor ("torso", "piernas", "pies"), que
 * tienen traducción en los doce archivos desde siempre —`tCat("zona",
 * …)`— y nadie estaba llamando.
 *
 * El guardián de textos crudos de test_i18n §7 no podía verlo: escanea
 * lo que hay ENTRE etiquetas JSX y en props visibles, y esto vive
 * adentro de una expresión.
 *
 * La conjunción sale de `comun.y`, así que cada idioma pone la suya.
 * Para el chino y el japonés el separador va sin espacios; queda
 * anotado para la revisión nativa junto con el resto.
 */
export function unirY(lista) {
  const xs = (lista || []).filter(Boolean).map(String);
  if (xs.length === 0) return "";
  if (xs.length === 1) return xs[0];
  const y = t("comun.y", "y");
  return `${xs.slice(0, -1).join(", ")} ${y} ${xs[xs.length - 1]}`;
}
